import React from 'react';
import {
    FormControl, Select, Grid, TextField, Paper, TableBody, TableCell,
    Button, TableContainer, Table, Typography, MenuItem, TableHead, TableRow,
    IconButton, Box, SelectChangeEvent, Stack, alpha, Divider, Chip,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { Delete, Add, Search as SearchIcon, Save as SaveIcon, Lock as LockIcon } from '@mui/icons-material';
import axios from 'axios';
import { getToken } from '../../Service/Service';
import { BASE_URL } from '../../common/constant';
import InvoiceCard from '../../components/InvoiceCard';
import AgentSearch from '../../common/AgentSearch';
import UpdateTable from '../../components/UpdateTable';
import toast from 'react-hot-toast';
import { PRIMARY } from 'themes/colors';
import { ConfirmDialogContext } from '../../common/ConfirmDialogProvider';

const TEAL = PRIMARY.main;
const TEAL_DARK = PRIMARY.dark;

const SectionLabel = ({ label }: { label: string }) => (
    <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 1.75, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}
    >
        {label}
    </Typography>
);

const FieldLabel = ({ label }: { label: string }) => (
    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
        {label}
    </Typography>
);

const ReadonlyValueBox = ({
    value,
    color = TEAL,
    bg,
}: {
    value: string | number;
    color?: string;
    bg?: string;
}) => (
    <Box
        sx={{
            px: 1.5, py: 1,
            borderRadius: 1.5,
            bgcolor: bg || alpha(color, 0.07),
            border: `1px solid ${alpha(color, 0.2)}`,
            minHeight: 40,
            display: 'flex', alignItems: 'center',
        }}
    >
        <Typography variant="body2" fontWeight={700} color={color}>
            {value}
        </Typography>
    </Box>
);

interface IRecord {
    id: number;
    invoiceId?: string;
    recordDate: Date;
    dname: string;
    item: string;
    agent: string;
    quantity: number;
    rate: number;
    totalAmt: number;
    paidCash: number;
    paidOnline: number;
    dueAmt: number;
    remarks: string;
    fare: number;
    totalFare: number;
    finalAmount: number;
    cylinderReceived: number;
}

const RefillUpdate = () => {
    const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

    const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(dayjs(new Date()));
    const [requestData, setRequestData] = React.useState({
        invoiceType: '',
        invoiceNo: null,
        startDate: null,
        endDate: null,
    });
    const [sale, setSale] = React.useState({
        item: '',
        date: '',
        agent: '',
        quantity: 0,
        totalAmount: 0,
        paidCash: 0,
        paidOnline: 0,
        dueAmount: 0,
        remarks: '',
        finalAmount: 0,
        rate: 0,
        cylinderReceived: 0,
        totalCylinderCount: 0,
    });
    const [selectedAgent, setSelectedAgent] = React.useState('');
    const [selectedRate, setSelectedRate] = React.useState<any>({});
    const [selectedFare, setSelectedFare] = React.useState(0);
    const [selectedInvoice, setSelectedInvoice] = React.useState<any>(null);
    const [itemQuantityMap, setItemQuantityMap] = React.useState<{ [key: string]: number }>({});
    const [invoices, setInvoices] = React.useState<any[]>([]);
    const [recordData, setRecordData] = React.useState<IRecord[]>([]);
    const [uniformFare, setUniformFare] = React.useState<number | null>(null);
    const [isCylinderReceivedManuallySet, setIsCylinderReceivedManuallySet] = React.useState(false);
    const [agentList, setAgentList] = React.useState<any[]>([]);
    const [agentListLoading, setAgentListLoading] = React.useState(false);

    const getRemainingQuantity = (itemType: string) => {
        const normalizedItemType = itemType.toUpperCase();
        const selectedItem = selectedInvoice?.items?.find(
            (item: any) =>
                item.item?.toUpperCase() === normalizedItemType ||
                item.item?.toUpperCase() === normalizedItemType.replace('KG', '') + 'KG' ||
                item.item?.toUpperCase().replace('KG', '') === normalizedItemType.replace('KG', '')
        );
        const usedQuantity = itemQuantityMap[itemType] || 0;
        return selectedItem ? selectedItem.shippedQuantity - usedQuantity : 0;
    };

    const validateQuantity = (itemType: string, quantity: number): boolean => {
        const remaining = getRemainingQuantity(itemType);
        return quantity <= remaining;
    };

    const updateItemQuantityMap = (itemType: string, quantity: number) => {
        setItemQuantityMap((prev) => ({ ...prev, [itemType]: (prev[itemType] || 0) + quantity }));
    };

    const handleCardSelect = (invoice: any) => {
        setSelectedInvoice(invoice);
        setItemQuantityMap({});
        setSale((prevSale) => ({ ...prevSale, dName: invoice.driverName || '' }));
    };

    const clearForm = () => {
        //@ts-ignore
        setSale({ item: '', paidCash: 0, paidOnline: 0, remarks: '' });
    };

    React.useEffect(() => {
        getAllAgents();
    }, []);

    const handleChange = (event: SelectChangeEvent<any> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        if (name === 'cylinderReceived') setIsCylinderReceivedManuallySet(true);

        setSale((prevSale) => {
            let updatedSale = { ...prevSale, [name as string]: value };
            if (name === 'quantity' && !isCylinderReceivedManuallySet) {
                updatedSale.cylinderReceived = value as any;
            }
            if (name === 'item') {
                const normalizedValue = (value as string).toUpperCase();
                switch (normalizedValue) {
                    case '14.2_KG_REFILL': updatedSale.rate = selectedRate?.kg14_refill || 0; break;
                    case '14.2_KG_NC': updatedSale.rate = selectedRate?.kg14_nc || 0; break;
                    case '14.2_KG_DBC': updatedSale.rate = selectedRate?.kg14_dbc || 0; break;
                    case '19_KG_REFILL': updatedSale.rate = selectedRate?.kg19_refill || 0; break;
                    case '19_KG_NC': updatedSale.rate = selectedRate?.kg19_nc || 0; break;
                    case '5KG_FTL_NC': updatedSale.rate = selectedRate?.kg5_ftl_nc || 0; break;
                    case '5KG_DOMESTIC': updatedSale.rate = selectedRate?.kg5_domestic || 0; break;
                    case '5KG_FTL_REFILL': updatedSale.rate = selectedRate?.kg5_ftl_refill || 0; break;
                    case 'fare': updatedSale.rate = selectedRate?.fare || 0; break;
                    // eslint-disable-next-line no-fallthrough
                    case 'totalCylinderCount':
                        //@ts-ignore
                        updatedSale.totalCylinderCount = selectedRate?.totalCylinderCount || 0; break;
                    default: updatedSale.rate = 0;
                }
            }
            return updatedSale;
        });
    };

    const handleDateChange = (date: Dayjs | null) => setSelectedDate(date);

    async function getAllAgents() {
        setAgentListLoading(true);
        try {
            const result = await axios.get(BASE_URL + 'agent/getall/active', {
                headers: { encryption: false, token: getToken() },
            });
            if (result.data.data.agents.length !== 0) {
                const agents = result.data.data.agents.map((agent: any) => ({
                    label: agent.name, value: agent.name,
                    fare: agent.fare,
                    kg14_refill: agent.kg14_refill, kg14_nc: agent.kg14_nc, kg14_dbc: agent.kg14_dbc,
                    kg19_refill: agent.kg19_refill, kg19_nc: agent.kg19_nc,
                    kg5_ftl_refill: agent.kg5_ftl_refill, kg5_ftl_nc: agent.kg5_ftl_nc, kg5_domestic: agent.kg5_domestic,
                    refillCount: agent.refillCount,
                    cylinderReceived14KG: agent.cylinderReceived14KG,
                    cylinderReceived19KG: agent.cylinderReceived19KG,
                    cylinderReceived5KG: agent.cylinderReceived5KG,
                }));
                setAgentList(agents);
            }
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Failed to fetch agents.');
        } finally {
            setAgentListLoading(false);
        }
    }

    const itemTotal = () => sale.rate * sale.quantity;
    const totalFare = () => (uniformFare !== null ? uniformFare : selectedFare) * sale.quantity;
    const totalPaid = () => Number(sale.paidCash) + Number(sale.paidOnline);
    const dueAmount = () => itemTotal() - totalPaid();
    const finalAmount = () => dueAmount() + totalFare();

    const handleAgentSelect = (agent: any) => {
        if (typeof agent === 'string') {
            setSelectedAgent(agent);
            setSelectedRate({});
            setSelectedFare(0);
            setSale((prev) => ({ ...prev, rate: 0, agent }));
        } else if (agent) {
            setSelectedAgent(agent.value);
            setSelectedRate(agent);
            setSelectedFare(agent.fare);
            if (sale.item) {
                sale.agent = agent.value;
                const ni = sale.item.toUpperCase();
                let newRate = 0;
                if (ni === '14.2_KG_REFILL') newRate = agent.kg14_refill;
                else if (ni === '14.2_KG_NC') newRate = agent.kg14_nc;
                else if (ni === '14.2_KG_DBC') newRate = agent.kg14_dbc;
                else if (ni === '19_KG_REFILL') newRate = agent.kg19_refill;
                else if (ni === '19_KG_NC') newRate = agent.kg19_nc;
                else if (ni === '5KG_FTL_REFILL') newRate = agent.kg5_ftl_refill;
                else if (ni === '5KG_FTL_NC') newRate = agent.kg5_ftl_nc;
                else if (ni === '5KG_DOMESTIC') newRate = agent.kg5_domestic;
                else if (ni === 'fare') newRate = agent.fare;
                setSale((prev) => ({ ...prev, rate: newRate || 0 }));
            }
        } else {
            setSelectedAgent('');
            setSelectedRate({});
            setSelectedFare(0);
            setSale((prev) => ({ ...prev, rate: 0 }));
        }
    };

    const saveRowData = () => {
        const ensureNumber = (v: any) => { const n = Number(v); return isNaN(n) ? 0 : n; };

        if (!sale.item) { toast('Please select an item type', { icon: '⚠️' }); return; }
        const quantity = ensureNumber(sale.quantity);
        if (!validateQuantity(sale.item, quantity)) {
            toast(`Cannot exceed available quantity. Remaining ${sale.item}: ${getRemainingQuantity(sale.item)}`, { icon: '⚠️' });
            return;
        }
        if (!selectedAgent || sale.rate <= 0 || itemTotal() <= 0) {
            toast('Please fill in all fields correctly', { icon: '⚠️' });
            return;
        }
        if (recordData.length === 0) setUniformFare(selectedFare);
        const fareToUse = uniformFare !== null ? uniformFare : selectedFare;

        const newData: IRecord = {
            id: recordData.length + 1,
            invoiceId: selectedInvoice.invoiceNo,
            item: sale.item,
            recordDate: selectedDate ? selectedDate.toDate() : new Date(),
            dname: selectedInvoice.driverName || '',
            agent: selectedAgent,
            quantity,
            rate: ensureNumber(sale.rate),
            totalAmt: ensureNumber(itemTotal()),
            paidCash: ensureNumber(sale.paidCash),
            paidOnline: ensureNumber(sale.paidOnline),
            dueAmt: ensureNumber(dueAmount()),
            remarks: sale.remarks,
            fare: ensureNumber(fareToUse),
            totalFare: ensureNumber(fareToUse * quantity),
            finalAmount: ensureNumber(dueAmount() + fareToUse * quantity),
            cylinderReceived: sale.cylinderReceived ? ensureNumber(sale.cylinderReceived) : 0,
        };

        if (selectedAgent === sale.agent && newData.item === sale.item) {
            toast('You have already inserted for this agent', { icon: '⚠️' });
        }

        updateItemQuantityMap(sale.item, quantity);
        setRecordData((prev) => [...prev, newData]);
        toast.success('Row added successfully');
        clearForm();
    };

    const removeRowData = (indexToRemove: number) => {
        showConfirmDialog('Remove Row', 'Are you sure you want to remove this row?', () => {
            setRecordData((prev) => {
                const removed = prev.find((i) => i.id === indexToRemove);
                if (removed) {
                    setItemQuantityMap((p) => ({ ...p, [removed.item]: (p[removed.item] || 0) - removed.quantity }));
                }
                const nd = prev.filter((i) => i.id !== indexToRemove);
                if (nd.length === 0) setUniformFare(null);
                return nd.map((i, idx) => ({ ...i, id: idx }));
            });
            toast.success('Row removed successfully');
        });
    };

    const validateInvoiceQuantities = () => {
        if (!selectedInvoice?.items) { toast.error('No invoice items found'); return false; }
        const invoiceItemsMap = new Map<string, number>();
        for (const item of selectedInvoice.items) invoiceItemsMap.set(item.item.toUpperCase(), item.shippedQuantity);
        const usedQuantities = new Map<string, number>();
        for (const record of recordData) {
            const ni = record.item.toUpperCase();
            usedQuantities.set(ni, (usedQuantities.get(ni) || 0) + record.quantity);
        }
        for (const [item, quantity] of usedQuantities.entries()) {
            if (!invoiceItemsMap.has(item)) { toast.error(`Item ${item} not found in invoice`); return false; }
            if (quantity > (invoiceItemsMap.get(item) || 0)) { toast.error(`Quantity for ${item} exceeds invoice`); return false; }
        }
        for (const [item, quantity] of invoiceItemsMap.entries()) {
            const used = usedQuantities.get(item) || 0;
            if (used !== quantity) { toast.error(`Quantity mismatch for ${item}. Invoice: ${quantity}, Used: ${used}`); return false; }
        }
        return true;
    };

    const updateHandle = async () => {
        if (!validateInvoiceQuantities()) return;
        showConfirmDialog('Are you sure to save these records', 'Do you want to save refill entries?', async () => {
            const result = await axios.post(BASE_URL + 'refillsale/add', recordData, {
                headers: { encryption: false, token: getToken() },
            });
            if (result.data) {
                toast.success(result.data.message);
                setRecordData([]);
                setUniformFare(null);
                setItemQuantityMap({});
            }
        });
    };

    const calculateSum = (data: IRecord[]) =>
        data.reduce(
            (acc, e) => {
                const n = (v: any) => { const x = Number(v); return isNaN(x) ? 0 : x; };
                acc.quantity += n(e.quantity);
                acc.totalSum += n(e.totalAmt);
                acc.fareSum += n(e.totalFare);
                acc.cashSum += n(e.paidCash);
                acc.onlineSum += n(e.paidOnline);
                acc.dueAdvance += n(e.dueAmt);
                acc.finalAmtSum += n(e.finalAmount);
                return acc;
            },
            { quantity: 0, totalSum: 0, fareSum: 0, cashSum: 0, onlineSum: 0, dueAdvance: 0, finalAmtSum: 0 }
        );

    const handleInvoiceTypeChange = (event: SelectChangeEvent<string>) => {
        setRequestData((prev) => ({ ...prev, invoiceType: event.target.value }));
    };
    const handleInvoiceNoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRequestData((prev) => ({ ...prev, invoiceNo: event.target.value as any }));
    };

    const handleFindInvoice = async () => {
        const { invoiceType, invoiceNo } = requestData;
        let payload: any = {};
        if (invoiceNo) payload = { findkey: 'invoiceNo', invoiceNo };
        else if (invoiceType) payload = { findkey: 'status', status: invoiceType };
        try {
            const result = await axios.post(BASE_URL + 'invoice/get', payload, {
                headers: { encryption: false, token: getToken() },
            });
            if (result.data?.result?.data) {
                toast.success('Fetched invoices successfully');
                setInvoices(result.data.result.data);
            }
        } catch {
            toast.error('No record found');
        }
    };

    const sums = calculateSum(recordData);

    const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.5 }, width: '100%' };
    const selectSx = { borderRadius: 1.5 };

    return (
        <Box>
            {/* ── Invoice Search ── */}
            <Paper
                elevation={0}
                sx={{ p: { xs: 2, sm: 2.5 }, mb: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}
            >
                <SectionLabel label="Find Invoice" />
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={6} md={4}>
                        <FieldLabel label="Invoice No" />
                        <TextField
                            fullWidth size="small"
                            value={requestData.invoiceNo ?? ''}
                            onChange={handleInvoiceNoChange}
                            name="invoiceNo" type="number"
                            sx={inputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FieldLabel label="Invoice Status" />
                        <FormControl fullWidth size="small">
                            <Select
                                value={requestData.invoiceType}
                                onChange={handleInvoiceTypeChange}
                                name="invoiceType"
                                sx={selectSx}
                                displayEmpty
                            >
                                <MenuItem value=""><em>Select status</em></MenuItem>
                                <MenuItem value="release">Release</MenuItem>
                                <MenuItem value="accept">Accept</MenuItem>
                                <MenuItem value="sale_captured">Sale Captured</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FieldLabel label="Invoice Date" />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={selectedDate}
                                onChange={handleDateChange}
                                slotProps={{
                                    textField: { size: 'small', fullWidth: true, sx: inputSx },
                                    openPickerIcon: { sx: { color: 'primary.main' } },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained" color="primary"
                            onClick={handleFindInvoice}
                            startIcon={<SearchIcon />}
                            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, px: 3, boxShadow: 'none' }}
                        >
                            Find Invoice
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* ── Release invoice table ── */}
            {requestData.invoiceType === 'release' && invoices.length > 0 && (
                <Box mb={2.5}>
                    <UpdateTable records={invoices} />
                </Box>
            )}

            {/* ── Invoice Cards ── */}
            {requestData.invoiceType !== 'release' && invoices.length > 0 && (
                <Box mb={2.5}>
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                        <Typography
                            variant="subtitle2" color="text.secondary"
                            sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}
                        >
                            Invoices
                        </Typography>
                        <Chip
                            label={`${invoices.filter((i) => i.status === 'accept' || i.status === 'sale_captured').length} found`}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(TEAL, 0.08), color: TEAL_DARK, fontWeight: 600 }}
                        />
                    </Stack>
                    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                        {invoices
                            .filter((inv) => inv.status === 'accept' || inv.status === 'sale_captured')
                            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                            .map((invoice) => (
                                <Box key={invoice.invoiceNo} sx={{ flexShrink: 0, width: 290 }}>
                                    <InvoiceCard
                                        data={invoice}
                                        onSelect={handleCardSelect}
                                        isSelected={selectedInvoice?.invoiceNo === invoice.invoiceNo}
                                        isAnySelected={selectedInvoice !== null}
                                    />
                                </Box>
                            ))}
                    </Box>
                </Box>
            )}

            {/* ── Already Captured Notice ── */}
            {selectedInvoice?.status === 'sale_captured' && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5, mb: 2.5, borderRadius: 2,
                        border: '1px solid', borderColor: alpha('#9e9e9e', 0.4),
                        bgcolor: 'grey.50',
                        display: 'flex', alignItems: 'center', gap: 1.5,
                    }}
                >
                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <LockIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                            Invoice #{selectedInvoice.invoiceNo} — Sale Already Captured
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                            This invoice has already been processed. Select an invoice with <strong>Accept</strong> status to enter refill data.
                        </Typography>
                    </Box>
                </Paper>
            )}

            {/* ── Refill Entry Form ── */}
            {selectedInvoice?.status === 'accept' && (
                <Paper
                    elevation={0}
                    sx={{ p: { xs: 2, sm: 2.5 }, mb: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}
                >
                    <SectionLabel label="Refill Entry" />

                    {/* Row 1 */}
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={12} sm={6} md={2}>
                            <FieldLabel label="Date" />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    slotProps={{ textField: { size: 'small', fullWidth: true, sx: inputSx } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FieldLabel label="Driver Name" />
                            <TextField
                                fullWidth size="small"
                                value={selectedInvoice?.driverName || ''}
                                InputProps={{ readOnly: true }}
                                sx={{ ...inputSx, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'grey.50' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FieldLabel label="Agent" />
                            <AgentSearch onSelectAgent={handleAgentSelect} list={agentList} loading={agentListLoading} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FieldLabel label="Item Type" />
                            <FormControl fullWidth size="small">
                                <Select value={sale.item} onChange={handleChange} name="item" sx={selectSx} displayEmpty>
                                    <MenuItem value=""><em>Select item</em></MenuItem>
                                    <MenuItem value="14.2_KG_REFILL">14.2 KG REFILL</MenuItem>
                                    <MenuItem value="14.2_KG_NC">14.2 KG NC</MenuItem>
                                    <MenuItem value="14.2_KG_DBC">14.2 KG DBC</MenuItem>
                                    <MenuItem value="19_KG_REFILL">19 KG REFILL</MenuItem>
                                    <MenuItem value="19_KG_NC">19 KG NC</MenuItem>
                                    <MenuItem value="5KG_FTL_REFILL">5KG FTL REFILL</MenuItem>
                                    <MenuItem value="5KG_FTL_NC">5KG FTL NC</MenuItem>
                                    <MenuItem value="5KG_DOMESTIC">5KG DOMESTIC</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FieldLabel label="Refill Qty" />
                            <TextField
                                fullWidth size="small"
                                value={sale.quantity}
                                onChange={handleChange}
                                name="quantity"
                                error={Boolean(sale.item && !validateQuantity(sale.item, Number(sale.quantity)))}
                                helperText={sale.item ? `Available: ${getRemainingQuantity(sale.item)}` : 'Select item first'}
                                sx={inputSx}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 2 }} />

                    {/* Row 2 */}
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Cylinder Received" />
                            <TextField
                                fullWidth size="small" type="number"
                                value={sale.cylinderReceived}
                                onChange={handleChange} name="cylinderReceived"
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Rate" />
                            <TextField
                                fullWidth size="small" type="number"
                                value={sale.rate}
                                onChange={handleChange} name="rate"
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Total Amount" />
                            <ReadonlyValueBox value={`₹${itemTotal()}`} color={TEAL_DARK} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Paid Cash" />
                            <TextField
                                fullWidth size="small" type="number"
                                value={sale.paidCash} onChange={handleChange} name="paidCash"
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Paid Online" />
                            <TextField
                                fullWidth size="small" type="number"
                                value={sale.paidOnline} onChange={handleChange} name="paidOnline"
                                sx={inputSx}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Due / Advance" />
                            <ReadonlyValueBox
                                value={`₹${dueAmount()}`}
                                color={dueAmount() > 0 ? '#c62828' : '#2e7d32'}
                                bg={dueAmount() > 0 ? alpha('#f44336', 0.07) : alpha('#4caf50', 0.07)}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 2 }} />

                    {/* Row 3 */}
                    <Grid container spacing={2} mb={2.5}>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Fare" />
                            <ReadonlyValueBox value={`₹${uniformFare !== null ? uniformFare : selectedFare}`} color="#7b1fa2" bg={alpha('#9c27b0', 0.07)} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Total Fare" />
                            <ReadonlyValueBox value={`₹${totalFare()}`} color="#7b1fa2" bg={alpha('#9c27b0', 0.07)} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Final Amount" />
                            <ReadonlyValueBox value={`₹${finalAmount()}`} color={TEAL_DARK} />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <FieldLabel label="Total Cylinder" />
                            <TextField
                                fullWidth size="small" type="number"
                                name="totalCylinderCount" disabled
                                sx={{ ...inputSx, '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'grey.50' } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8} md={4}>
                            <FieldLabel label="Remarks" />
                            <TextField
                                fullWidth size="small"
                                value={sale.remarks} onChange={handleChange} name="remarks"
                                placeholder="Optional remarks..."
                                sx={inputSx}
                            />
                        </Grid>
                    </Grid>

                    <Button
                        variant="contained" color="primary"
                        onClick={saveRowData}
                        startIcon={<Add />}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, px: 3, boxShadow: 'none' }}
                    >
                        Add Row
                    </Button>
                </Paper>
            )}

            {/* ── Records Table ── */}
            {recordData.length > 0 && (
                <Paper
                    elevation={0}
                    sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', overflow: 'hidden', mb: 2 }}
                >
                    <Box
                        sx={{
                            px: 2.5, py: 1.5,
                            borderBottom: '1px solid', borderColor: 'grey.200',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            variant="subtitle2" color="text.secondary"
                            sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}
                        >
                            Refill Records
                        </Typography>
                        <Chip
                            label={`${recordData.length} row${recordData.length > 1 ? 's' : ''}`}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(TEAL, 0.08), color: TEAL_DARK, fontWeight: 600 }}
                        />
                    </Box>

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: TEAL_DARK }}>
                                    {['Date', 'Driver', 'Item', 'Agent', 'Qty', 'Empty Cyl', 'Rate', 'Total', 'Cash', 'Online', 'Due', 'Remarks', 'Fare', 'Total Fare', 'Final Amt', ''].map((h) => (
                                        <TableCell
                                            key={h}
                                            sx={{ color: '#fff', fontWeight: 700, whiteSpace: 'nowrap', fontSize: 12, borderBottom: 'none', py: 1.25 }}
                                        >
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recordData.map((record) => (
                                    <TableRow
                                        key={record.agent + record.id}
                                        sx={{
                                            '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                                            '&:hover': { bgcolor: alpha(TEAL, 0.04) },
                                            '& td': { fontSize: 12, whiteSpace: 'nowrap', border: 0, py: 0.75 },
                                        }}
                                    >
                                        <TableCell>{record.recordDate?.toLocaleDateString()}</TableCell>
                                        <TableCell>{record.dname}</TableCell>
                                        <TableCell>{record.item}</TableCell>
                                        <TableCell>{record.agent}</TableCell>
                                        <TableCell>{record.quantity}</TableCell>
                                        <TableCell>{record.cylinderReceived}</TableCell>
                                        <TableCell>{record.rate}</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: TEAL_DARK }}>₹{record.totalAmt}</TableCell>
                                        <TableCell>{record.paidCash}</TableCell>
                                        <TableCell>{record.paidOnline}</TableCell>
                                        <TableCell sx={{ color: record.dueAmt > 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
                                            ₹{record.dueAmt}
                                        </TableCell>
                                        <TableCell>{record.remarks}</TableCell>
                                        <TableCell>{record.fare}</TableCell>
                                        <TableCell>₹{record.totalFare}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: TEAL_DARK }}>₹{record.finalAmount}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => removeRowData(record.id)}>
                                                <Delete fontSize="small" color="error" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* Totals row */}
                                <TableRow sx={{ bgcolor: alpha(TEAL, 0.06), '& td': { fontWeight: 700, fontSize: 12, py: 1, border: 0 } }}>
                                    <TableCell colSpan={4}>Totals</TableCell>
                                    <TableCell>{sums.quantity}</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell>₹{sums.totalSum}</TableCell>
                                    <TableCell>₹{sums.cashSum}</TableCell>
                                    <TableCell>₹{sums.onlineSum}</TableCell>
                                    <TableCell>₹{sums.dueAdvance}</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell>—</TableCell>
                                    <TableCell>₹{sums.fareSum}</TableCell>
                                    <TableCell>₹{sums.finalAmtSum}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ px: 2.5, py: 1.75, borderTop: '1px solid', borderColor: 'grey.200' }}>
                        <Button
                            variant="contained"
                            onClick={updateHandle}
                            startIcon={<SaveIcon />}
                            sx={{
                                textTransform: 'none', borderRadius: 2, fontWeight: 600,
                                bgcolor: TEAL, '&:hover': { bgcolor: TEAL_DARK }, boxShadow: 'none',
                            }}
                        >
                            Save Refill Table
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default RefillUpdate;
